import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy, Download, FileText, ShieldCheck } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConsoleCard } from "@/components/ui/console";
import { InfoBanner } from "@/components/ui/banner";
import { KVList } from "@/components/data-display/kv-list";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import { useTransferStore } from "@/stores/transfer-store";
import {
  EVIDENCE_ACTOR_FILTERS,
  EVIDENCE_PACKETS,
  EVIDENCE_STATS,
  EVIDENCE_TIMELINE,
  EVIDENCE_TYPE_FILTERS,
  EXPORT_CONFIG,
  EXPORT_RECEIPT,
  PACKET_META,
  PACKET_TIMELINE,
} from "./oversight-data";
import { EmptyRow, EventTimeline, Panel, ToneBadge } from "./oversight-ui";

const DATE_FILTERS = [
  { value: "7d", label: "Last 7 days" },
  { value: "24h", label: "Last 24h" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All time" },
];

const ACTOR_OPTIONS = EVIDENCE_ACTOR_FILTERS.map((a) => ({ value: a === "All Actors" ? "" : a, label: a }));

const EXPORT_REFERENCE_TS = Date.parse("2026-09-15T10:45:00Z");

function createdTs(created: string) {
  const [date, time] = created.split(" ");
  return Date.parse(`${date}T${time}:00Z`);
}

function withinDateRange(created: string, range: string) {
  if (range === "all") return true;
  const windows: Record<string, number> = { "24h": 24 * 3600e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 };
  const w = windows[range];
  return !!w && EXPORT_REFERENCE_TS - createdTs(created) <= w;
}

function useCopy() {
  return React.useCallback((label: string, value: string) => {
    const done = () => toast({ variant: "success", title: "Copied to clipboard", description: label });
    const fail = () =>
      toast({ variant: "warning", title: "Copy unavailable", description: "Clipboard access was blocked by the browser." });
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(value).then(done).catch(fail);
    } else {
      fail();
    }
  }, []);
}

export function EvidenceOverview() {
  const [type, setType] = React.useState("");
  const [actor, setActor] = React.useState("");
  const [range, setRange] = React.useState("7d");
  const [query, setQuery] = React.useState("");

  const filtered = React.useMemo(
    () =>
      EVIDENCE_PACKETS.filter((p) => {
        const q = query.toLowerCase();
        const actorMatch = !actor || (actor === "System" ? p.actor.includes("System") : p.actor === actor);
        return (
          (!q || `${p.id} ${p.subject} ${p.type} ${p.actor}`.toLowerCase().includes(q)) &&
          (!type || p.type === type) &&
          actorMatch &&
          withinDateRange(p.created, range)
        );
      }),
    [type, actor, range, query]
  );

  return (
    <div>
      <PageHeader
        title="Evidence Center"
        subtitle="Immutable, sealed records of every material action across the platform."
      />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {EVIDENCE_STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} />
        ))}
      </div>

      <FilterBar className="mb-5">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search packets by ID, subject, actor…" />
        <FilterSelect value={type} onChange={setType} options={EVIDENCE_TYPE_FILTERS} ariaLabel="Filter by type" />
        <FilterSelect value={actor} onChange={setActor} options={ACTOR_OPTIONS} ariaLabel="Filter by actor" />
        <FilterSelect value={range} onChange={setRange} options={DATE_FILTERS} ariaLabel="Filter by date range" />
        <Button variant="secondary" size="sm" type="button">
          Search
        </Button>
      </FilterBar>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">Packet ID</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Subject</th>
              <th className="px-4 py-2.5">Created (UTC)</th>
              <th className="px-4 py-2.5">Actor</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <EmptyRow colSpan={7}>No evidence packets match your filters.</EmptyRow>
            ) : (
              filtered.map((p) => (
                <tr key={p.id} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3">
                    <Link to={`/evidence/${p.id}`} className="font-mono text-[13px] font-semibold text-primary hover:underline">
                      {p.id}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <ToneBadge tone="info" dot={false}>{p.type}</ToneBadge>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-ink">{p.subject}</td>
                  <td className="px-4 py-3 text-[13px] text-muted">{p.created}</td>
                  <td className="px-4 py-3 text-[13px] text-text">{p.actor}</td>
                  <td className="px-4 py-3">
                    <ToneBadge tone={p.statusTone}>{p.statusLabel}</ToneBadge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={p.linkHref} className="text-xs font-semibold text-primary hover:underline">
                      {p.linkLabel}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}

function EvidenceLadder({ events }: { events: typeof EVIDENCE_TIMELINE.events }) {
  const copy = useCopy();
  return (
    <div className="divide-y divide-console-line">
      {events.map((e, i) => (
        <div key={`${e.ts}-${e.title}`} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0">
          <span className="w-6 shrink-0 text-right text-console-muted">{String(i + 1).padStart(2, "0")}</span>
          <span className="w-[68px] shrink-0 text-console-muted">{e.ts}</span>
          <span
            aria-hidden
            className={cn(
              "mt-[7px] size-1.5 shrink-0 rounded-full",
              e.tone === "success" && "bg-console-success",
              e.tone === "warning" && "bg-console-warning",
              e.tone === "danger" && "bg-console-danger",
              e.tone === "info" && "bg-console-info"
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="font-bold text-console-text">{e.title}</span>
            <span className="text-console-muted"> · {e.detail}</span>
            {e.hash && (
              <span className="mt-0.5 block truncate text-[11px] text-console-muted/80">{e.hash}</span>
            )}
          </span>
          {e.hash && (
            <button
              type="button"
              onClick={() => copy(`${e.title} integrity hash`, e.hash as string)}
              className="inline-flex shrink-0 items-center gap-1 rounded border border-console-line px-1.5 py-0.5 text-[11px] text-console-muted transition-colors hover:text-console-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
            >
              <Copy aria-hidden className="size-3" /> copy
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function useGeneratedReceipt(id: string) {
  const transferDetails = useTransferStore((s) => s.transferDetails);
  return React.useMemo(
    () => Object.values(transferDetails).find((d) => d.evidenceReceipt?.evidenceId === id) ?? null,
    [transferDetails, id]
  );
}

function PacketDetailActions({ packet }: { packet: (typeof EVIDENCE_PACKETS)[number] }) {
  const isReference = packet.id === PACKET_META.id;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Link to={packet.linkHref} className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
        {isReference ? "Open transfer" : packet.linkLabel} <ArrowRight className="size-3.5" aria-hidden />
      </Link>
      {isReference && (
        <>
          <Link to={PACKET_META.approvalHref} className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
            View approval APR-001 <ArrowRight className="size-3.5" aria-hidden />
          </Link>
          <Link to="/reconciliation" className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
            Reconciliation <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        </>
      )}
      <Link to="/evidence/packet" className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
        Compliance Packet <ArrowRight className="size-3.5" aria-hidden />
      </Link>
      <Link to="/evidence/export" className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
        Audit Export
      </Link>
    </div>
  );
}

function PacketMetaBody({ packet }: { packet: (typeof EVIDENCE_PACKETS)[number] }) {
  const isReference = packet.id === PACKET_META.id;
  return (
    <Panel title="Packet" bodyClassName="px-5 py-4">
      <KVList
        rows={[
          { k: "Packet ID", v: <span className="font-mono">{packet.id}</span> },
          { k: "Type", v: <ToneBadge tone="info" dot={false}>{packet.type}</ToneBadge> },
          {
            k: "Subject",
            v: isReference ? PACKET_META.subject : packet.subject,
          },
          { k: "Actor", v: isReference ? PACKET_META.actor : packet.actor },
          { k: "Created", v: isReference ? PACKET_META.created : `${packet.created} UTC` },
          ...(isReference
            ? [
                {
                  k: "Sealed",
                  v: (
                    <span className="inline-flex items-center gap-2">
                      <span>{PACKET_META.sealed}</span>
                      <ToneBadge tone="success">Sealed</ToneBadge>
                    </span>
                  ),
                },
              ]
            : [{ k: "Status", v: <ToneBadge tone={packet.statusTone}>{packet.statusLabel}</ToneBadge> }]),
        ]}
      />
      <div className="mt-4">
        <PacketDetailActions packet={packet} />
      </div>
    </Panel>
  );
}

function PacketReferenceTabs() {
  return (
    <Panel bodyClassName="px-5 pb-5 pt-1">
      <Tabs defaultValue="policy">
        <TabsList className="mb-4">
          <TabsTrigger value="policy">Policy &amp; Risk</TabsTrigger>
          <TabsTrigger value="screening">Screening</TabsTrigger>
          <TabsTrigger value="provider">Provider Metadata</TabsTrigger>
          <TabsTrigger value="mesh">Mesh Metadata</TabsTrigger>
        </TabsList>
        <TabsContent value="policy">
          <div className="grid gap-5 lg:grid-cols-2">
            <Panel title="Policy Evaluation Receipt" bodyClassName="p-0">
              <KVList rows={PACKET_META.receipt} />
            </Panel>
            <Panel title="Screening Result (Chainalysis)" bodyClassName="p-0">
              <KVList rows={[...PACKET_META.screening, { k: "Result", v: "Sanctions match: false" }]} />
            </Panel>
          </div>
        </TabsContent>
        <TabsContent value="screening">
          <KVList
            rows={[
              { k: "Provider", v: "Chainalysis KYT" },
              { k: "Address", v: "0x7a2B…f4E1" },
              { k: "Result", v: "Sanctions match: false" },
              { k: "Timestamp", v: "2026-09-15 09:46 UTC" },
            ]}
          />
        </TabsContent>
        <TabsContent value="provider">
          <KVList
            rows={[
              { k: "Provider", v: "Fireblocks" },
              { k: "Model", v: "Custody + MPC signing" },
              { k: "Networks", v: "Ethereum mainnet" },
            ]}
          />
        </TabsContent>
        <TabsContent value="mesh">
          <KVList
            rows={[
              { k: "Wallet", v: "WLT-003 (HT Treasury Cold)" },
              { k: "Mesh", v: "VaultCore HSM-01" },
              { k: "Signing", v: "Fireblocks MPC" },
            ]}
          />
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

function GeneratedReceiptView({ id }: { id: string }) {
  const detail = useGeneratedReceipt(id);
  if (!detail || !detail.evidenceReceipt) {
    return (
      <div>
        <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: id }]} />
        <Panel>
          <div className="px-6 py-12 text-center">
            <p className="text-sm font-semibold text-ink">Evidence packet {id} was not found.</p>
            <p className="mt-1 text-xs text-muted">It may not have been sealed yet. Return to the Evidence Center.</p>
            <Link to="/evidence" className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "mt-4")}>
              Back to Evidence Center
            </Link>
          </div>
        </Panel>
      </div>
    );
  }

  const receipt = detail.evidenceReceipt;
  const by = detail.rejection?.by ?? detail.createdBy ?? "System";
  const disposition = receipt.disposition;

  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: id }]} />
      <PageHeader
        title={`TXI-${detail.id.replace("TXI-", "")} — ${disposition}`}
        subtitle="Evidence receipt generated at rejection time."
        actions={<ToneBadge tone="success">Complete</ToneBadge>}
      />
      <InfoBanner variant="info" title="Generated record">
        This receipt was sealed when the transfer was rejected with evidence capture enabled. It is linked from the
        transfer record and resolves from the live transfer store, so it is not part of the canonical ledger fixtures.
      </InfoBanner>

      <Panel title="Packet" bodyClassName="px-5 py-4">
        <KVList
          rows={[
            { k: "Packet ID", v: <span className="font-mono">{id}</span> },
            { k: "Type", v: <ToneBadge tone="info" dot={false}>Transfer</ToneBadge> },
            { k: "Subject", v: `TXI-${detail.id.replace("TXI-", "")} — ${disposition}` },
            { k: "Actor", v: by },
            { k: "Disposition", v: disposition },
            { k: "Status", v: <ToneBadge tone="success">Sealed</ToneBadge> },
          ]}
        />
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/transfers/${detail.id}`} className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
            View transfer <ArrowRight className="size-3.5" aria-hidden />
          </Link>
          <Link to="/evidence" className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
            Audit Export
          </Link>
        </div>
      </Panel>
    </div>
  );
}

export function EvidenceDetail({ id }: { id: string }) {
  const packet = EVIDENCE_PACKETS.find((p) => p.id === id);

  if (!packet) {
    return <GeneratedReceiptView id={id} />;
  }

  const isReference = packet.id === PACKET_META.id;

  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: packet.id }]} />
      <PageHeader
        title={PACKET_META.subject}
        subtitle="Immutable, sealed record of every material action across the platform."
        actions={<ToneBadge tone={packet.statusTone}>{packet.statusLabel}</ToneBadge>}
      />

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <PacketMetaBody packet={packet} />
        </div>

        <div>
          {isReference ? (
            <div>
              <Panel title="Timeline" bodyClassName="px-5 py-4">
                <EventTimeline
                  events={PACKET_TIMELINE.map((e) => ({
                    title: e.title,
                    meta: e.detail,
                    tone: e.tone,
                    ts: e.ts,
                  }))}
                />
              </Panel>
              <PacketReferenceTabs />
            </div>
          ) : (
            <Panel bodyClassName="px-5 py-4">
              <p className="text-sm font-semibold text-ink">Policy &amp; screening capture</p>
              <p className="mt-1 text-xs text-muted">
                Detailed policy and screening metadata is captured on the reference packet ({PACKET_META.id}). This
                packet records a single sealed event.
              </p>
              <Link to={`/evidence/${PACKET_META.id}`} className="mt-3 inline-block text-xs font-semibold text-primary hover:underline">
                Open the reference packet
              </Link>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

export function EvidencePacket() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: "Compliance Packet" }]} />
      <PageHeader
        title="Compliance Evidence Packet"
        subtitle={PACKET_META.subject}
        actions={
          <>
            <Link to={`/evidence/${PACKET_META.id}`} className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
              Open timeline
            </Link>
            <Link to="/evidence/export" className={cn(buttonVariants({ variant: "primary", size: "sm" }))}>
              <Download aria-hidden className="size-4" /> Export
            </Link>
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <Panel title={<span className="flex items-center gap-2"><FileText className="size-4 text-primary" aria-hidden />Packet</span>} bodyClassName="px-5 py-4">
            <KVList
              rows={[
                { k: "Packet", v: PACKET_META.id },
                { k: "Type", v: PACKET_META.type },
                { k: "Actor", v: PACKET_META.actor },
                { k: "Created", v: PACKET_META.created },
                { k: "Sealed", v: PACKET_META.sealed },
                {
                  k: "Transfer",
                  v: (
                    <Link to={PACKET_META.transferHref} className="text-primary hover:underline">
                      {PACKET_META.subject}
                    </Link>
                  ),
                },
                {
                  k: "Approval",
                  v: (
                    <Link to={PACKET_META.approvalHref} className="text-primary hover:underline">
                      APR-001
                    </Link>
                  ),
                },
              ]}
            />
          </Panel>
        </div>

        <div>
          <Panel bodyClassName="px-5 pb-5 pt-1">
            <Tabs defaultValue="timeline">
              <TabsList className="mb-4">
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="receipt">Receipt</TabsTrigger>
                <TabsTrigger value="screening">Screening</TabsTrigger>
              </TabsList>
              <TabsContent value="timeline">
                <EventTimeline
                  events={PACKET_TIMELINE.map((e) => ({
                    title: e.title,
                    meta: e.detail,
                    tone: e.tone,
                    ts: e.ts,
                  }))}
                />
              </TabsContent>
              <TabsContent value="receipt">
                <KVList rows={PACKET_META.receipt} />
              </TabsContent>
              <TabsContent value="screening">
                <KVList rows={[...PACKET_META.screening, { k: "Result", v: "Sanctions match: false" }]} />
              </TabsContent>
            </Tabs>
          </Panel>

          <Panel title="Sealed chain-of-custody" bodyClassName="px-5 py-4">
            <InfoBanner variant="info" title="Illustrative fixture">
              Chain-of-custody ladder shown for the reference transfer intent. Values are prototype fixtures, not live data.
            </InfoBanner>
            <ConsoleCard title={`evidence-ladder — ${PACKET_META.id}`} meta="sealed" className="mt-4">
              <EvidenceLadder events={EVIDENCE_TIMELINE.events} />
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-console-line pt-3 text-[11px] text-console-muted">
                <ShieldCheck aria-hidden className="size-3.5 text-console-success" />
                Chain integrity verified · {EVIDENCE_TIMELINE.events.length}/{EVIDENCE_TIMELINE.events.length} events sealed
              </div>
            </ConsoleCard>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export function AuditExport() {
  const [format, setFormat] = React.useState<(typeof EXPORT_CONFIG.formats)[number]>("JSON");
  const [generated, setGenerated] = React.useState(false);
  const copy = useCopy();

  const preview = React.useMemo(() => {
    const payload = EVIDENCE_PACKETS.map((p) => ({
      id: p.id,
      type: p.type,
      subject: p.subject,
      created: createdTs(p.created) ? `${p.created}:00Z` : p.created,
      actor: p.actor,
      status: p.status,
    }));
    return JSON.stringify(payload, null, 2);
  }, []);

  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: "Audit Export" }]} />
      <PageHeader title="Audit Export" subtitle="Generate a sealed, tamper-evident bundle of evidence packets for auditors." />

      <Panel title="Export Configuration" bodyClassName="px-5 py-4">
        <KVList
          rows={[
            { k: "Date range", v: EXPORT_CONFIG.dateRange },
            { k: "Packet types", v: EXPORT_CONFIG.packetTypes },
            { k: "Initiated by", v: EXPORT_CONFIG.initiatedBy },
          ]}
        />

        <div className="mt-5">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.06em] text-muted">Format</div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Export format">
            {EXPORT_CONFIG.formats.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={format === f}
                onClick={() => setFormat(f)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                  format === f
                    ? "border-primary bg-primary-soft text-primary"
                    : "border-line bg-surface text-text hover:border-muted"
                )}
              >
                {format === f && <Check aria-hidden className="size-3.5" />}
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button
            size="sm"
            onClick={() => {
              setGenerated(true);
              toast({
                variant: "success",
                title: `Export generated as ${format}`,
                description: localOnly(`${EXPORT_RECEIPT.id} · ${EXPORT_RECEIPT.packetCount} packets`),
              });
            }}
          >
            <Download aria-hidden className="size-4" /> Generate Export
          </Button>
          <Link to="/evidence" className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
            Cancel
          </Link>
        </div>
      </Panel>

      <Panel title="Export Preview — 8 packets" bodyClassName="px-5 py-4">
        <pre className="overflow-x-auto rounded-lg border border-line bg-bg px-4 py-3 font-mono text-[11px] leading-relaxed text-text">
          {preview}
        </pre>
      </Panel>

      {generated && (
        <Panel title="Export Receipt" bodyClassName="px-5 py-4">
          <KVList
            rows={[
              { k: "Receipt", v: EXPORT_RECEIPT.id },
              { k: "Format", v: format },
              { k: "Generated", v: EXPORT_RECEIPT.generated },
              { k: "Actor", v: EXPORT_RECEIPT.actor },
              { k: "Packets", v: String(EXPORT_RECEIPT.packetCount) },
              { k: "Integrity hash", v: EXPORT_RECEIPT.hash },
            ]}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => copy("Export integrity hash", EXPORT_RECEIPT.hash)}>
              <Copy aria-hidden className="size-4" /> Copy hash
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                toast({ variant: "success", title: `Download started (${format})`, description: localOnly(EXPORT_RECEIPT.id) })
              }
            >
              <Download aria-hidden className="size-4" /> Download
            </Button>
          </div>
        </Panel>
      )}
    </div>
  );
}